import { Box, Tab, Tabs as MuiTabs } from '@mui/material';
import type { ReactElement } from 'react';

import { displayValue } from './displayValue';

/**
 * Onglets avec compteur facultatif (maquette « BO/Onglet »).
 *
 * Les `Tabs` de MUI apportent le comportement attendu d'une liste d'onglets —
 * fleches gauche et droite, Debut et Fin, un seul arret de tabulation,
 * `role="tablist"`/`tab` et `aria-selected`. Le kit fixe l'apparence (libelle
 * gras a l'actif, soulignement de 2 px) et le **compteur** : `count` absent ->
 * pas de pastille ; `count: null` -> « — », parce que le serveur n'a pas rendu
 * de nombre et qu'un zero serait une mesure inventee.
 *
 * Le contenu de l'onglet actif est rendu par l'appelant ; `panelId` le relie a
 * l'onglet selectionne (`aria-controls`).
 */
export interface TabItem<V extends string> {
  readonly value: V;
  readonly label: string;
  readonly count?: number | null;
}

export interface TabsProps<V extends string> {
  /** Nom de la liste d'onglets, pour les lecteurs d'ecran. */
  readonly label: string;
  readonly value: V;
  readonly onChange: (value: V) => void;
  readonly items: readonly TabItem<V>[];
  readonly panelId?: string;
}

export const Tabs = <V extends string>({ label, value, onChange, items, panelId }: TabsProps<V>): ReactElement => (
  <MuiTabs
    aria-label={label}
    value={value}
    onChange={(_event, next: V) => {
      onChange(next);
    }}
    variant="scrollable"
    scrollButtons={false}
    sx={{
      minHeight: 40,
      borderBottom: 1,
      borderColor: 'tokens.border',
      '& .MuiTabs-indicator': { height: 2, bgcolor: 'tokens.primary' },
      '& .MuiTabs-list': { gap: 3 },
    }}
  >
    {items.map((item) => {
      const active = item.value === value;

      return (
        <Tab
          key={item.value}
          value={item.value}
          aria-controls={active ? panelId : undefined}
          // Nom explicite : sans lui, le libelle et le compte se lisent colles
          // (« Toutes12 »).
          aria-label={item.count === undefined ? item.label : `${item.label} (${displayValue(item.count)})`}
          label={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              {item.label}
              {item.count === undefined ? null : (
                <Box
                  component="span"
                  sx={{
                    px: '7px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: '16px',
                    bgcolor: active ? 'tokens.primarySoft' : 'tokens.neutralSoft',
                    color: active ? 'tokens.primary' : 'tokens.textMuted',
                  }}
                >
                  {displayValue(item.count)}
                </Box>
              )}
            </Box>
          }
          sx={{
            minHeight: 40,
            minWidth: 0,
            px: 0.25,
            color: 'tokens.textMuted',
            '&.Mui-selected': { color: 'tokens.textStrong', fontWeight: 700 },
          }}
        />
      );
    })}
  </MuiTabs>
);
